import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Activity, Beaker, TrendingUp, Loader2 } from "lucide-react";
import OverviewTab from "@/components/cardiology/OverviewTab";
import BPTab from "@/components/cardiology/BPTab";
import LipidsTab from "@/components/cardiology/LipidsTab";
import RiskTab from "@/components/cardiology/RiskTab";

/* ------------------------------------------------------------------ */
/* Cardiology dashboard — 4 tabs                                      */
/*   1. Overview   — latest BP + latest lipid + latest ASCVD/HEART    */
/*   2. BP Log     — add reading + history + 90-day trend             */
/*   3. Lipids     — add panel + history + trend chart                */
/*   4. Risk Scores — ASCVD 10-yr PCE + HEART Score (ED chest pain)   */
/* ------------------------------------------------------------------ */

export default function Cardiology() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";
  const [tab, setTabState] = useState(initialTab);
  const setTab = (t) => {
    setTabState(t);
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== tab) setTabState(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [bp, setBp] = useState({ readings: [], targets: null });
  const [lipids, setLipids] = useState({ panels: [], targets: null });
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [bpRes, lipRes] = await Promise.all([
        api.get("/specialties/cardiology/bp"),
        api.get("/specialties/cardiology/lipids"),
      ]);
      setBp(bpRes.data || { readings: [] });
      setLipids(lipRes.data || { panels: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load cardiology data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const latestBp = bp.readings[0];
  const latestLipid = lipids.panels[0];

  const TABS = [
    { key: "overview", label: "Overview", icon: Heart },
    { key: "bp",       label: "BP Log",   icon: Activity },
    { key: "lipids",   label: "Lipids",   icon: Beaker },
    { key: "risk",     label: "Risk Scores", icon: TrendingUp },
  ];

  return (
    <div className="space-y-6" data-testid="cardiology-page">
      <div className="ros-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
               style={{ background: "rgba(248,113,113,0.14)", border: "1px solid rgba(248,113,113,0.3)" }}>
            <Heart size={22} style={{ color: "#F87171" }} />
          </div>
          <div>
            <h2 className="font-heading text-2xl">Cardiology console</h2>
            <p className="text-sm text-[#A3ACA7] mt-0.5">
              Blood pressure, lipid trends, and validated cardiovascular risk scoring — AHA/ACC guidelines.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="cardio-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`cardio-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                active
                  ? "bg-[#F87171] text-black border-[#F87171]"
                  : "bg-[#0F1312] text-[#A3ACA7] border-[#272E2B] hover:text-white hover:border-[#F87171]/40"
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center text-[#A3ACA7] py-16 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading cardiology data…
        </div>
      ) : (
        <>
          {tab === "overview" && (
            <OverviewTab latestBp={latestBp} latestLipid={latestLipid}
                         bpCount={bp.readings.length} lipidCount={lipids.panels.length}
                         onQuickJump={setTab} />
          )}
          {tab === "bp"     && <BPTab data={bp} onSaved={loadAll} />}
          {tab === "lipids" && <LipidsTab data={lipids} onSaved={loadAll} />}
          {tab === "risk"   && <RiskTab latestBp={latestBp} />}
        </>
      )}
    </div>
  );
}
